import React from 'react';
import { Box, Skeleton, Paper, Grid } from '@mui/material';

// Skeleton for dashboard stat cards
export const StatCardSkeleton = () => (
  <Paper elevation={6} sx={{ p: 3, borderRadius: 3, boxShadow: 6 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width={80} height={32} />
        <Skeleton variant="text" width={120} height={20} />
      </Box>
    </Box>
  </Paper>
);

// Skeleton for chart components
export const ChartSkeleton = ({ height = 220 }) => (
  <Paper elevation={4} sx={{ p: 3, borderRadius: 3, boxShadow: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
      <Skeleton variant="text" width={200} height={24} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: 1 }} />
  </Paper>
);

// Skeleton for booking cards
export const BookingCardSkeleton = () => (
  <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="text" width={200} height={20} />
        <Skeleton variant="text" width={160} height={20} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
      <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 3 }} />
      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 3 }} />
    </Box>
  </Paper>
);

// Skeleton for table rows
export const TableRowSkeleton = ({ columns = 6 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
    {Array.from({ length: columns }).map((_, index) => (
      <Box key={index} sx={{ flex: 1, pr: 2 }}>
        <Skeleton variant="text" width="80%" height={20} />
      </Box>
    ))}
  </Box>
);

// Skeleton for review cards
export const ReviewCardSkeleton = () => (
  <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Skeleton variant="text" width={120} height={24} />
      <Skeleton variant="rectangular" width={120} height={20} sx={{ ml: 1, borderRadius: 1 }} />
    </Box>
    <Skeleton variant="text" width="100%" height={20} />
    <Skeleton variant="text" width="80%" height={20} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
      <Skeleton variant="text" width={100} height={16} />
      <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
    </Box>
  </Paper>
);

// Combined dashboard skeleton
export const DashboardSkeleton = () => (
  <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: { xs: 1, md: 3 } }}>
    <Skeleton variant="text" width={300} height={40} sx={{ mb: 3, mx: 'auto' }} />
    
    {/* Stat Cards */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <StatCardSkeleton />
        </Grid>
      ))}
    </Grid>

    {/* Charts */}
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <ChartSkeleton />
      </Grid>
      <Grid item xs={12} md={6}>
        <ChartSkeleton />
      </Grid>
    </Grid>

    {/* Recent Bookings */}
    <Box sx={{ mt: 4 }}>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
      {Array.from({ length: 3 }).map((_, index) => (
        <BookingCardSkeleton key={index} />
      ))}
    </Box>
  </Box>
);

export default {
  StatCardSkeleton,
  ChartSkeleton,
  BookingCardSkeleton,
  TableRowSkeleton,
  ReviewCardSkeleton,
  DashboardSkeleton
};